/**
 * Modal for editing a scheduled event (task, action item, habit
 * occurrence, or recurring instance) from /schedule-v2.
 *
 * Opens in two contexts:
 *   1. Click a pill -> open with the event's current values; "was X"
 *      diff indicators appear as the user edits fields.
 *   2. Drag-drop a pill to a new time -> parent pre-fills the modal
 *      with the proposed date/time/duration as the draft, keeps the
 *      original values in `initial` so the diff indicators highlight
 *      exactly what changed.
 *
 * For recurring items the user picks a "scope" up-front (this
 * occurrence only / all occurrences) via radio-card buttons, then
 * saves once. This is clearer than forcing a choice between two
 * separate "Save" buttons at the bottom — the semantic weight of the
 * decision belongs at decision time, not at commit time.
 *
 * The caller decides which API each decision hits; this component just
 * reports the user's choice + the edits they made via `onSave`.
 */

import React, { useEffect, useMemo, useState } from 'react'
import {
  Calendar,
  Clock,
  Timer,
  Repeat,
  X,
  RotateCcw,
  Check,
  AlertCircle,
} from 'lucide-react'
import { format } from 'date-fns'
import { Dialog } from '../ui/Dialog'
import type { CommitmentState } from '../../services/scheduleService'
import { CommitmentChip, CommitmentGlyph } from './CommitmentBadge'
import { PRODUCT_NAME } from './productName'

export type SaveScope = 'single' | 'this_occurrence' | 'all_occurrences'

export interface EventEditValues {
  title: string
  description?: string
  /** YYYY-MM-DD */
  date: string
  /** HH:MM (24h) */
  startTime: string
  durationMin: number
  /**
   * SC-7 secondary. Moving a block confirms it by default; this is the
   * user's explicit "move it, but it stays Horton's to adjust". Only ever
   * set from the opt-out below, and only offered on a Flexible block whose
   * placement actually changed.
   */
  keepFlexible?: boolean
}

export interface EventEditInitial extends EventEditValues {
  /** Recurrence metadata — drives which save buttons are shown. */
  isRecurring: boolean
  /** Human-readable label under the title ("Habit occurrence", "Recurring event"). */
  recurrenceLabel?: string
  /** Optional draft state the parent wants to pre-fill the form with
   *  (e.g. from a drag-drop). When omitted, the form starts at the
   *  original values. Diffs are always measured against the original
   *  (initial) values, regardless of starting draft. */
  proposed?: Partial<EventEditValues>
}

interface EventEditModalProps {
  open: boolean
  initial: EventEditInitial | null
  saving?: boolean
  error?: string | null
  /**
   * The block's current calendar commitment, when the caller knows it.
   * `null`/undefined keeps the modal silent about it — no chip, no
   * "keep flexible" opt-out — rather than guessing.
   */
  commitment?: CommitmentState | null
  onSave: (scope: SaveScope, edits: EventEditValues) => void | Promise<void>
  onCancel: () => void
}

// ───────────────────────────────────────────────────────────────────────
// Formatting helpers
// ───────────────────────────────────────────────────────────────────────

function formatDurationLabel(minutes: number): string {
  if (!minutes || minutes <= 0) return '0m'
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function formatDateLabel(iso: string): string {
  // iso is YYYY-MM-DD; parse as local, not UTC, to avoid off-by-one
  // when crossing midnight in the user's timezone.
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return format(new Date(y, m - 1, d), 'EEE, MMM d')
}

function formatTimeLabel(hhmm: string): string {
  const [hh, mm] = hhmm.split(':').map(Number)
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return hhmm
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  return format(d, 'h:mm a')
}

// ───────────────────────────────────────────────────────────────────────
// Small subcomponents
// ───────────────────────────────────────────────────────────────────────

/**
 * Field wrapper that:
 *   - shows a label with icon
 *   - paints a soft amber border + "changed" dot when modified
 *   - exposes a one-click reset when modified
 *
 * We use amber, not red, for the modified state because red reads as
 * "error" and these fields are valid — they're just unsaved. Amber is
 * the universal "modified, pending commit" signal (VS Code dirty dot,
 * Git status, etc.).
 */
interface FieldShellProps {
  icon: React.ReactNode
  label: string
  changed: boolean
  originalLabel?: string
  onReset?: () => void
  children: React.ReactNode
}

const FieldShell: React.FC<FieldShellProps> = ({
  icon,
  label,
  changed,
  originalLabel,
  onReset,
  children,
}) => (
  <div
    className={`rounded-xl border p-3 transition-colors ${
      changed
        ? 'border-warn/60 bg-warn/5'
        : 'border-soft bg-surface'
    }`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-secondary uppercase tracking-wide">
        <span className="text-muted">{icon}</span>
        {label}
        {changed && (
          <span
            aria-hidden
            className="inline-block w-1.5 h-1.5 rounded-full bg-warn animate-pulse"
          />
        )}
      </div>
      {changed && onReset && (
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-app-micro text-muted hover:text-secondary px-1.5 py-0.5 rounded transition-colors"
          title={`Reset to ${originalLabel ?? 'original'}`}
        >
          <RotateCcw className="w-3 h-3" />
          reset
        </button>
      )}
    </div>
    {children}
    {changed && originalLabel && (
      <div className="mt-1.5 text-app-micro text-muted">
        was{' '}
        <span className="line-through decoration-muted decoration-1">
          {originalLabel}
        </span>
      </div>
    )}
  </div>
)

/**
 * Large scope-picker card used for recurring items. One card per
 * choice; the selected one gets a brand border + check mark. This is
 * way less ambiguous than two "Save" buttons side by side.
 */
interface ScopeCardProps {
  selected: boolean
  label: string
  description: string
  icon: React.ReactNode
  onClick: () => void
}

const ScopeCard: React.FC<ScopeCardProps> = ({
  selected,
  label,
  description,
  icon,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`flex-1 flex items-start gap-3 text-left p-3 rounded-xl border-2 transition-all ${
      selected
        ? 'border-brand bg-brand-subtle shadow-sm'
        : 'border-soft bg-surface hover:border-brand-subtle-border hover:bg-surface-alt/40'
    }`}
  >
    <span
      className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
        selected ? 'border-brand bg-brand text-white' : 'border-strong'
      }`}
    >
      {selected && <Check className="w-3 h-3" strokeWidth={3} />}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-primary">
        {icon}
        {label}
      </div>
      <div className="text-xs text-secondary mt-0.5 leading-snug">
        {description}
      </div>
    </div>
  </button>
)

// ───────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────

export const EventEditModal: React.FC<EventEditModalProps> = ({
  open,
  initial,
  saving = false,
  error,
  commitment,
  onSave,
  onCancel,
}) => {
  // Local draft state. Re-seeded whenever a NEW event is opened
  // (identified by the shape of `initial`) so flipping between pills
  // resets the form.
  const seedKey = useMemo(() => {
    if (!initial) return 'none'
    return JSON.stringify({
      t: initial.title,
      d: initial.date,
      s: initial.startTime,
      m: initial.durationMin,
      p: initial.proposed ?? null,
    })
  }, [initial])

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [durationMin, setDurationMin] = useState<number>(30)
  const [scope, setScope] = useState<SaveScope>('this_occurrence')
  const [keepFlexible, setKeepFlexible] = useState(false)

  useEffect(() => {
    if (!initial) return
    const p = initial.proposed ?? {}
    setTitle(p.title ?? initial.title)
    setDescription(p.description ?? initial.description ?? '')
    setDate(p.date ?? initial.date)
    setStartTime(p.startTime ?? initial.startTime)
    setDurationMin(p.durationMin ?? initial.durationMin)
    // SC-7's default is confirm-on-move; the opt-out is always a fresh,
    // deliberate act per edit, never a remembered preference.
    setKeepFlexible(false)
    // Default scope: drag-drop of a recurring item nearly always means
    // "just this one" (one-off reschedule). Click-to-edit is the same
    // default — users rarely intend to change every habit ever.
    setScope(initial.isRecurring ? 'this_occurrence' : 'single')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey])

  if (!initial) return null

  const changedTitle = title !== initial.title
  const changedDescription = (description || '') !== (initial.description || '')
  const changedDate = date !== initial.date
  const changedStart = startTime !== initial.startTime
  const changedDuration = durationMin !== initial.durationMin
  const whenChanged = changedDate || changedStart || changedDuration
  const anyChange =
    changedTitle || changedDescription || changedDate || changedStart || changedDuration

  const changeCount =
    (changedTitle ? 1 : 0) +
    (changedDescription ? 1 : 0) +
    (changedDate ? 1 : 0) +
    (changedStart ? 1 : 0) +
    (changedDuration ? 1 : 0)

  // SC-7: the opt-out is only meaningful when the user is re-placing a
  // block Horton still owns. It is deliberately absent on a Confirmed
  // block (nothing to keep), on a block whose commitment we don't know
  // (we won't invent one), and on a title/notes-only edit — which is
  // exactly when the server's own placement-change gate declines to
  // confirm anything either.
  const offerKeepFlexible = commitment === 'flexible' && whenChanged

  const submit = () => {
    if (!anyChange) {
      onCancel()
      return
    }
    void onSave(scope, {
      title: title.trim() || initial.title,
      description: description.trim() || undefined,
      date,
      startTime,
      durationMin: Math.max(5, Math.round(durationMin)),
      keepFlexible: offerKeepFlexible && keepFlexible ? true : undefined,
    })
  }

  const isRecurring = initial.isRecurring
  const recurrenceBadge = initial.recurrenceLabel
    ? initial.recurrenceLabel
    : isRecurring
    ? 'Recurring'
    : 'One-off'

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel() }}>
      {/* Custom content wrapper — we don't use DialogContent directly
          because we want a full-bleed sticky header/footer with an
          internally-scrolling body. */}
      <div
        className={
          'bg-surface shadow-strong border border-soft flex flex-col ' +
          'w-full rounded-t-2xl sm:rounded-2xl sm:max-w-xl sm:mx-4 ' +
          'max-h-[calc(100vh-var(--sat,0px)-var(--sab,0px)-16px)] ' +
          'sm:max-h-[85vh]'
        }
      >
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-soft">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-app-micro font-semibold ${
                  isRecurring
                    ? 'bg-brand-subtle text-brand border border-brand-subtle-border'
                    : 'bg-surface-alt text-secondary border border-soft'
                }`}
              >
                {isRecurring && <Repeat className="w-3 h-3" />}
                {recurrenceBadge}
              </span>
              <CommitmentChip state={commitment} />
              {anyChange && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-app-micro font-semibold bg-warn/15 text-warn border border-warn/30">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-warn animate-pulse" />
                  {changeCount} unsaved
                </span>
              )}
            </div>
            <h2 className="text-lg font-semibold text-primary truncate">
              {title.trim() || initial.title}
            </h2>
            <p className="text-xs text-secondary mt-0.5">
              {formatDateLabel(initial.date)} · {formatTimeLabel(initial.startTime)} ·{' '}
              {formatDurationLabel(initial.durationMin)}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="flex-shrink-0 p-1.5 rounded-lg text-muted hover:text-primary hover:bg-surface-alt transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body (scrolls) ───────────────────────────────────────── */}
        <form
          id="event-edit-form"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
        >
          {/* When-it-changes summary: only shown when the date/time
              changed, as a pronounced "A → B" pill pair. This is where
              drag-drop's proposed time shows up the moment the modal
              opens. */}
          {whenChanged && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-warn/10 border border-warn/30 text-sm">
              <AlertCircle className="w-4 h-4 text-warn flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-secondary line-through decoration-muted">
                  {formatDateLabel(initial.date)} · {formatTimeLabel(initial.startTime)}
                </span>
                <span className="mx-2 text-warn font-bold">→</span>
                <span className="font-semibold text-primary">
                  {formatDateLabel(date)} · {formatTimeLabel(startTime)}
                </span>
              </div>
            </div>
          )}

          {/* SC-7 secondary: moving a Flexible block confirms it unless the
              user says otherwise. One checkbox, inside the edit the user
              already opened — never a separate approval step, and never
              shown for a block that is already the user's (SC-6). */}
          {offerKeepFlexible && (
            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-soft bg-surface-alt/40 cursor-pointer hover:border-brand-subtle-border transition-colors">
              <input
                type="checkbox"
                checked={keepFlexible}
                onChange={(e) => setKeepFlexible(e.target.checked)}
                className="mt-0.5 accent-[var(--brand-primary)]"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
                  <CommitmentGlyph state="flexible" className="w-3.5 h-3.5" />
                  Keep it flexible
                </span>
                <span className="block text-xs text-secondary mt-0.5 leading-snug">
                  {keepFlexible
                    ? `Moving it here for now. ${PRODUCT_NAME} can still shift it around your day.`
                    : `Saving makes this time yours — ${PRODUCT_NAME} stops moving it. Tick this to move it and leave the rest to ${PRODUCT_NAME}.`}
                </span>
              </span>
            </label>
          )}

          {/* Title */}
          <FieldShell
            icon={<span className="text-app-nano font-bold">T</span>}
            label="Title"
            changed={changedTitle}
            originalLabel={initial.title || '—'}
            onReset={() => setTitle(initial.title)}
          >
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-transparent text-primary text-sm focus:outline-none placeholder:text-muted"
              placeholder="Title"
            />
          </FieldShell>

          {/* When — date + time side-by-side, duration below. The three
              are visually grouped so users parse "when" as one concept
              rather than three disconnected fields. */}
          <div className="grid grid-cols-2 gap-2">
            <FieldShell
              icon={<Calendar className="w-3.5 h-3.5" />}
              label="Date"
              changed={changedDate}
              originalLabel={formatDateLabel(initial.date)}
              onReset={() => setDate(initial.date)}
            >
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-transparent text-primary text-sm focus:outline-none"
              />
            </FieldShell>
            <FieldShell
              icon={<Clock className="w-3.5 h-3.5" />}
              label="Start"
              changed={changedStart}
              originalLabel={formatTimeLabel(initial.startTime)}
              onReset={() => setStartTime(initial.startTime)}
            >
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full bg-transparent text-primary text-sm focus:outline-none tabular-nums"
              />
            </FieldShell>
          </div>

          <FieldShell
            icon={<Timer className="w-3.5 h-3.5" />}
            label="Duration"
            changed={changedDuration}
            originalLabel={formatDurationLabel(initial.durationMin)}
            onReset={() => setDurationMin(initial.durationMin)}
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={5}
                step={5}
                value={durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value))}
                className="w-20 bg-transparent text-primary text-sm focus:outline-none tabular-nums"
              />
              <span className="text-xs text-muted">minutes</span>
              {/* Quick duration presets — save a round-trip to the
                  number spinner for the 90% case. */}
              <div className="ml-auto flex gap-1">
                {[15, 30, 45, 60].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDurationMin(m)}
                    className={`px-2 py-0.5 rounded text-app-micro font-medium transition-colors ${
                      durationMin === m
                        ? 'bg-brand text-white'
                        : 'bg-surface-alt text-secondary hover:bg-surface-alt/60'
                    }`}
                  >
                    {m < 60 ? `${m}m` : '1h'}
                  </button>
                ))}
              </div>
            </div>
          </FieldShell>

          {/* Description — collapsed into a single compact field; shown
              expanded only when the user starts typing or there's
              existing content, so the form isn't visually dominated by
              an empty textarea for the common "no notes" case. */}
          <FieldShell
            icon={<span className="text-app-nano font-bold">¶</span>}
            label="Notes"
            changed={changedDescription}
            originalLabel={initial.description || '(empty)'}
            onReset={() => setDescription(initial.description ?? '')}
          >
            <textarea
              rows={description || changedDescription ? 3 : 1}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-transparent text-primary text-sm resize-none focus:outline-none placeholder:text-muted"
              placeholder="Add notes (optional)"
            />
          </FieldShell>

          {/* Scope picker — only for recurring items. The radio-cards
              make "this one" vs "the whole series" feel like two real
              choices, not a subtlety you might miss. */}
          {isRecurring && (
            <div className="pt-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-secondary uppercase tracking-wide mb-2">
                <Repeat className="w-3.5 h-3.5" />
                Apply changes to
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <ScopeCard
                  selected={scope === 'this_occurrence'}
                  onClick={() => setScope('this_occurrence')}
                  label="Only this occurrence"
                  description="One-time override. Future occurrences keep the original schedule."
                  icon={<Calendar className="w-3.5 h-3.5" />}
                />
                <ScopeCard
                  selected={scope === 'all_occurrences'}
                  onClick={() => setScope('all_occurrences')}
                  label="All occurrences"
                  description="Updates the recurring item itself. Affects every future instance."
                  icon={<Repeat className="w-3.5 h-3.5" />}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-bad/10 border border-bad/40 text-sm text-bad">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="min-w-0">{error}</span>
            </div>
          )}
        </form>

        {/* ── Footer ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-soft bg-surface-alt/40 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-secondary hover:bg-surface-alt disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="event-edit-form"
            disabled={saving || !anyChange}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              saving || !anyChange
                ? 'bg-surface-alt text-muted cursor-not-allowed'
                : 'bg-brand text-white hover:bg-brand-hover shadow-sm'
            }`}
          >
            {saving
              ? 'Saving…'
              : !anyChange
              ? 'No changes'
              : isRecurring
              ? scope === 'all_occurrences'
                ? 'Save all'
                : 'Save this one'
              : 'Save changes'}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

export default EventEditModal
